import { api, direRefus, parametres } from '../api.ts';
import { $, brut, euros, h, jour, mot, peindre, valeur, type Brut } from '../ui.ts';
import { estAdmin, peutEcrire, type Session } from '../etat.ts';
import { LIBELLES_CANDIDATURE, LIBELLES_MISSION, REFUS_CANDIDATURE, STATUTS_CANDIDATURE,
         STATUTS_MISSION, type StatutCandidature } from '../../domaine/mission.ts';

export interface LigneMission {
  id: string; reference: string; intitule: string; client: string | null; secteur: string | null;
  taille_client: string | null; region: string | null; role_grille: string | null; role_libelle: string | null;
  statut: string; demarrage: string | null; duree_texte: string | null;
  budget_min_eur: number | null; budget_max_eur: number | null; zones: string[];
  brief_le: string | null; cree_le: string; modifie_le: string;
  candidats?: number; presentes?: number;
}

interface Brief {
  enjeu: string; contexte?: string | null;
  // Depuis `brief-2`, un livrable porte son échéance. Les briefs écrits avant sont des
  // chaînes simples, et restent en base tels quels : l'affichage accepte les deux formes
  // plutôt que d'exiger une réécriture de toutes les missions pour une virgule.
  livrables: (string | { livrable: string; echeance?: string | null })[];
  competences_requises: string[]; competences_souhaitables: string[];
  intensite_ia?: string | null; ia_attendu?: string | null;
  criteres_succes: string[]; questions: string[];
  rattachement?: string | null; modalites?: string[];
}

const direLivrable = (l: Brief['livrables'][number]) =>
  typeof l === 'string' ? l : l.echeance ? `${l.livrable} — ${l.echeance}` : l.livrable;

interface Candidature {
  id: string; statut: StatutCandidature; motif: string | null; score: number | null;
  evaluation: unknown; evalue_le: string | null;
  /** D'où vient ce candidat. Le moteur n'existe pas encore : tout est 'manuelle' aujourd'hui. */
  origine: 'manuelle' | 'rapprochement'; ajoute_par: string | null;
  profil_id: string; prenom: string; nom: string; role_grille: string | null; titre: string | null;
  profil_statut: string; tjm_eur: number | null; dispo_texte: string | null; zones: string[];
}

interface DocumentMission {
  id: string; nom: string; origine: 'depose' | 'genere'; mime: string; taille: number;
  depose_le: string; depose_par: string | null;
}

const COLONNES = '110px 1fr 200px 150px 130px 120px';

const filtres = { q: '', statut: '', role: '', page: 1 };

const TEINTE: Record<string, string> = {
  brouillon: 'var(--basalt)', ouverte: 'var(--vert)', pourvue: 'var(--copper)',
  perdue: 'var(--rouge)', close: 'var(--basalt)',
};

export const budget = (m: LigneMission) =>
  m.budget_min_eur == null ? '—'
    : m.budget_max_eur && m.budget_max_eur !== m.budget_min_eur
      ? `${m.budget_min_eur} – ${euros(m.budget_max_eur)}` : euros(m.budget_min_eur);

const ligne = (m: LigneMission) => h`
  <button class="tr" data-act="mission" data-id="${m.id}" style="grid-template-columns:${COLONNES}">
    <span class="sm sourd" style="font-variant-numeric:tabular-nums">${m.reference}</span>
    <div><div style="font-size:13.5px;color:var(--obsidian);font-weight:bold">${m.intitule}</div>
      <div class="sm sourd" style="margin-top:3px">${m.role_libelle ?? '—'}</div></div>
    <span class="sm">${m.client ?? m.secteur ?? '—'}</span>
    <div class="rang" style="gap:6px">
      <span class="point" style="background:${TEINTE[m.statut] ?? 'var(--basalt)'}"></span>
      <span style="font-size:12px">${LIBELLES_MISSION[m.statut as keyof typeof LIBELLES_MISSION] ?? m.statut}</span></div>
    <span class="sm">${jour(m.demarrage)}</span>
    <span class="sm sourd">${m.candidats ?? 0} candidat${(m.candidats ?? 0) > 1 ? 's' : ''}</span>
  </button>`;

export async function ecranMissions(session: Session, chrome: (corps: Brut) => Brut): Promise<void> {
  const charger = async () => {
    const r = await api.get<{ total: number; missions: LigneMission[] }>('/api/missions' + parametres(filtres));

    peindre('#app', chrome(h`
      <div class="page">
        <div class="entre"><div><h1 class="h1">Missions</h1>
          <p class="sous" style="margin:4px 0 0">${r.total} mission${r.total > 1 ? 's' : ''}</p></div>
          ${peutEcrire(session) ? h`<button class="bt p" data-act="nouvelle-mission">Nouvelle mission</button>` : ''}</div>

        <form id="rech-m" class="carte" style="display:flex;align-items:center;gap:10px;background:#FDFCFB;border-color:var(--line)">
          <input class="champ" id="qm" value="${filtres.q}" style="border:none;padding:0;font-size:13.5px"
            placeholder="Chercher — référence, intitulé, client, secteur…">
          <button class="bt p" type="submit">Rechercher</button></form>

        <div class="rang" style="flex-wrap:wrap;gap:5px">
          ${STATUTS_MISSION.map((s) => h`<button class="puce" aria-pressed="${filtres.statut === s}"
            data-act="filtre-m" data-valeur="${s}">${LIBELLES_MISSION[s]}</button>`)}
        </div>

        <div class="large"><div>
          <div class="th" style="grid-template-columns:${COLONNES}">
            ${['RÉFÉRENCE', 'MISSION · RÔLE', 'CLIENT', 'ÉTAT', 'DÉMARRAGE', 'CANDIDATS']
              .map((t) => h`<span>${t}</span>`)}</div>
          ${r.missions.length ? r.missions.map(ligne)
            : h`<p class="sm sourd" style="padding:30px 8px">Aucune mission ne répond à ces filtres.</p>`}
        </div></div>
      </div>`));

    $('#rech-m')?.addEventListener('submit', (ev) => {
      ev.preventDefault(); filtres.q = valeur('#qm'); filtres.page = 1; void charger();
    });
  };

  await charger().catch((e) => mot(direRefus(e)));
}

export const etatFiltresMissions = filtres;

// ─── Le dossier d'une mission ───────────────────────────────────────────────

const listeBloc = (titre: string, items: string[] | undefined, teinte = '') => items?.length
  ? h`<div style="margin-top:11px"><div class="pt sourd" style="letter-spacing:1.2px">${titre}</div>
      <ul style="margin:4px 0 0;padding-left:17px">${items.map((x) =>
        h`<li style="font-size:12.5px;line-height:1.55;${teinte}">${x}</li>`)}</ul></div>` : '';

const candidat = (c: Candidature, ecriture: boolean) => h`
  <div class="carte" style="margin-top:9px">
    <div class="rang" style="align-items:flex-start;gap:12px">
      <div class="pousse">
        <button data-act="profil" data-id="${c.profil_id}"
          style="font-size:13px;color:var(--obsidian);font-weight:bold">${c.prenom} ${c.nom}</button>
        <div class="sm sourd" style="margin-top:2px">${c.titre ?? '—'}
          · ${c.tjm_eur ? euros(c.tjm_eur) : 'taux non dit'} · ${c.dispo_texte ?? 'disponibilité non dite'}</div>
      </div>
      <span class="tri" title="${c.origine === 'rapprochement'
        ? 'Proposé par le moteur de rapprochement'
        : `Ajouté à la main${c.ajoute_par ? ` par ${c.ajoute_par}` : ''}`}"
        style="align-self:center">${c.origine === 'rapprochement' ? 'MOTEUR' : `MAIN${c.ajoute_par ? ` · ${c.ajoute_par}` : ''}`}</span>
      <span class="et ${c.statut === 'ecarte' || c.statut === 'refuse_client' ? 'a' : c.statut === 'repere' ? 'o' : 'v'}">
        ${LIBELLES_CANDIDATURE[c.statut]}</span>
      ${c.score != null ? h`<span class="sm" style="font-variant-numeric:tabular-nums">${c.score}/100</span>` : ''}
    </div>
    ${c.motif ? h`<div class="sm" style="margin-top:7px;border-left:2px solid var(--copper);padding-left:9px">${c.motif}</div>` : ''}
    ${ecriture ? h`<div class="rang" style="gap:7px;margin-top:9px;flex-wrap:wrap">
      <select class="champ" id="st-${c.id}" data-st="${c.id}" style="max-width:220px;font-size:12px">
        ${STATUTS_CANDIDATURE.map((s) => h`<option value="${s}" ${s === c.statut ? brut('selected') : ''}>${LIBELLES_CANDIDATURE[s]}</option>`)}
      </select>
      <input class="champ" id="mo-${c.id}" placeholder="Motif — obligatoire pour un refus"
        style="flex:1 1 220px;font-size:12px" ${REFUS_CANDIDATURE.includes(c.statut) ? '' : brut('hidden')}>
      <button class="bt s" data-act="cand" data-id="${c.id}">Enregistrer</button></div>` : ''}
  </div>`;

/**
 * Les documents de la mission. Un seul endroit, quelle que soit la provenance : celui qui
 * cherche la fiche à envoyer ne se demande pas si un collègue l'a déposée ou si MANA l'a
 * produite. L'origine se lit quand même, parce qu'on ne relit pas de la même façon ce
 * qu'on a écrit et ce qu'une machine a rédigé.
 */
const blocDocuments = (id: string, documents: DocumentMission[], aBrief: boolean,
                       ecriture: boolean): Brut => h`
  <section class="bloc-doc">
    <div class="filet-l"><span class="sur">DOCUMENTS · ${documents.length}</span>
      <span class="pt sourd pousse" style="text-align:right">la fiche qui part au candidat</span></div>

    ${documents.length ? h`<div style="margin-top:10px;display:flex;flex-direction:column;gap:9px">
      ${documents.map((x) => h`<div class="carte">
        <div style="font-size:12.5px;color:var(--obsidian)">${x.nom}</div>
        <div class="pt sourd">${x.origine === 'genere' ? 'produit par MANA' : 'déposé'}
          · ${jour(x.depose_le)}${x.depose_par ? ` · ${x.depose_par}` : ''}
          · ${Math.round(x.taille / 1024)} ko</div>
        <div class="rang" style="gap:7px;margin-top:8px">
          <button class="bt s" style="border-color:var(--obsidian)" data-act="doc-m-voir"
            data-id="${x.id}" data-nom="${x.nom}">Voir</button>
          <button class="bt s" data-act="doc-m-telecharger" data-id="${x.id}">Télécharger</button>
        </div></div>`)}</div>`
    : h`<p class="sm sourd" style="margin:10px 0 0">Aucun document. Déposez la fiche du client,
        ou produisez-la à partir du brief relu.</p>`}

    ${ecriture ? h`<form id="doc-mission" style="margin-top:11px;display:flex;flex-direction:column;gap:7px">
      <input class="champ" type="file" id="doc-m-fichier" accept=".pdf,.docx,.txt,.md" required>
      <button class="bt" type="submit" style="align-self:flex-start">Déposer un document</button></form>

      <div class="rang" style="margin-top:9px">
        <button class="bt p" data-act="produire-fiche" data-id="${id}" ${aBrief ? '' : brut('disabled')}>
          Produire la fiche</button></div>
      <p class="pt sourd" style="margin:6px 0 0">${aBrief
        ? h`Au modèle du cabinet, à partir du brief relu. Elle s'enregistre telle qu'elle est
            produite : celle qu'on a envoyée doit rester consultable même si le brief change.`
        : h`Il faut un brief relu avant de pouvoir produire une fiche.`}</p>` : ''}
  </section>`;

export async function ecranMission(id: string, session: Session, chrome: (corps: Brut) => Brut): Promise<void> {
  const d = await api.get<{ mission: LigneMission & { brief: Brief | null; besoin_brut: string | null;
    ponderations: Record<string, number> }; candidatures: Candidature[];
    documents: DocumentMission[] }>(`/api/missions/${id}`);
  const m = d.mission;
  const b = m.brief;
  const ecriture = peutEcrire(session);

  peindre('#app', chrome(h`
    <div class="page">
      <p class="sm sourd" style="margin:0"><button data-act="aller" data-vue="missions"
        style="color:var(--copper)">Missions</button> · ${m.reference}</p>

      <div class="entre" style="align-items:flex-start">
        <div>
          <div class="rang" style="flex-wrap:wrap;gap:10px">
            <h1 class="h1">${m.intitule}</h1>
            ${m.role_libelle ? h`<span class="et">${m.role_libelle}</span>` : ''}
            <span class="et ${m.statut === 'ouverte' ? 'v' : m.statut === 'perdue' ? 'a' : 'o'}">
              ${LIBELLES_MISSION[m.statut as keyof typeof LIBELLES_MISSION] ?? m.statut}</span>
          </div>
          <div class="rang" style="flex-wrap:wrap;gap:22px;margin-top:7px">
            <span class="sm">${m.client ?? '—'}${m.secteur ? ` · ${m.secteur}` : ''}</span>
            <span class="sm">Démarrage : ${jour(m.demarrage)}${m.duree_texte ? ` · ${m.duree_texte}` : ''}</span>
            <span class="sm">Budget de vente : ${budget(m)}</span>
            <span class="sm">${m.region ?? (m.zones.join(', ') || '—')}</span>
          </div>
        </div>
        ${ecriture ? h`<div class="rang" style="gap:7px">
          <select class="champ" id="statut-m" style="max-width:150px;font-size:12px">
            ${STATUTS_MISSION.map((s) => h`<option value="${s}" ${s === m.statut ? brut('selected') : ''}>${LIBELLES_MISSION[s]}</option>`)}
          </select>
          <button class="bt" data-act="statut-m" data-id="${id}">Changer l'état</button>
          ${estAdmin(session) && !d.candidatures.length
            ? h`<button class="bt" style="color:var(--rouge);border-color:var(--rouge)"
                  data-act="effacer-m" data-id="${id}" data-nom="${m.intitule}">Effacer</button>` : ''}
          </div>` : ''}
      </div>

      <div style="display:flex;gap:26px;align-items:flex-start;flex-wrap:wrap">
        <div style="flex:1 1 440px;min-width:0;display:flex;flex-direction:column;gap:18px">
          ${b ? h`<div class="enc st">
              <div class="rang"><span class="sur">LE BRIEF, RELU ET VALIDÉ</span>
                <span class="pt sourd">${jour(m.brief_le)}</span></div>
              <div style="margin-top:8px;font-size:13px;line-height:1.55">${b.enjeu}</div>
              ${listeBloc('COMPÉTENCES REQUISES', b.competences_requises)}
              ${listeBloc('SOUHAITABLES', b.competences_souhaitables, 'color:var(--basalt)')}
              ${b.intensite_ia ? h`<div style="margin-top:11px"><span class="et o">Posture IA attendue : ${b.intensite_ia}</span>
                ${b.ia_attendu ? h`<div class="sm" style="margin-top:5px">${b.ia_attendu}</div>` : ''}</div>` : ''}
              ${listeBloc('LIVRABLES', b.livrables.map(direLivrable))}
              ${listeBloc('CRITÈRES DE SUCCÈS', b.criteres_succes)}
              ${b.contexte ? h`<div style="margin-top:11px"><div class="pt sourd" style="letter-spacing:1.2px">CONTEXTE</div>
                <div style="font-size:12.5px;line-height:1.55;margin-top:3px">${b.contexte}</div></div>` : ''}
            </div>` : ''}

          ${b?.questions.length ? h`<div class="enc ar"><span class="sur">RESTE À DEMANDER AU CLIENT</span>
            <ul style="margin:6px 0 0;padding-left:17px">${b.questions.map((x) =>
              h`<li style="font-size:12.5px;line-height:1.5">${x}</li>`)}</ul></div>` : ''}

          <section>
            <div class="filet"><span class="sur">CANDIDATS · ${d.candidatures.length}</span>
              <span class="pt sourd pousse" style="text-align:right">${(() => {
                const moteur = d.candidatures.filter((c) => c.origine === 'rapprochement').length;
                const main = d.candidatures.length - moteur;
                return moteur
                  ? `${moteur} du moteur · ${main} à la main`
                  : "un refus exige son motif — c'est lui qui corrigera le rapprochement";
              })()}</span></div>
            ${ecriture ? h`<form id="rattacher" class="rang" style="gap:8px;margin-top:11px">
              <input class="champ" id="q-profil" style="flex:1 1 240px"
                placeholder="Rattacher quelqu'un — nom, rôle, expertise…">
              <button class="bt s p" type="submit">Chercher dans le vivier</button></form>
              <p class="pt sourd" style="margin:6px 0 0">Seuls les profils <b>qualifiés</b> se
                rattachent : présenter quelqu'un qu'on n'a pas vu en entretien n'engage pas
                que lui.</p>
              <div id="resultats"></div>` : ''}
            ${d.candidatures.length ? d.candidatures.map((c) => candidat(c, ecriture))
              : h`<p class="sm sourd" style="margin-top:11px">Aucun candidat rattaché pour l'instant.</p>`}
          </section>
        </div>

        <div style="flex:0 1 330px;min-width:260px;display:flex;flex-direction:column;gap:16px">
          ${blocDocuments(id, d.documents, Boolean(b), ecriture)}

          <div class="enc gl"><span class="sur">LE RAPPROCHEMENT AUTOMATIQUE</span>
            <p>Il arrive au temps suivant. Il retrouvera les profils par leurs expériences, les évaluera
               un par un sur cinq axes, et ne notera jamais sans citer sa source. En attendant, rattachez
               les candidats à la main : les motifs que vous écrirez serviront à le régler.</p></div>

          <div class="enc gl"><span class="sur">PONDÉRATIONS</span>
            <div style="margin-top:7px;display:flex;flex-direction:column;gap:3px">
              ${Object.entries(m.ponderations).map(([axe, poids]) =>
                h`<div class="rang sm"><span class="pousse">${axe.replace('_', ' ')}</span><b>${poids}</b></div>`)}
            </div>
            <p class="pt sourd" style="margin:7px 0 0">Le taux et la disponibilité filtrent. Ils ne notent pas.</p></div>

          ${m.besoin_brut ? h`<section><div class="filet-l"><span class="sur">LA DEMANDE D'ORIGINE</span></div>
            <div class="sm" style="margin-top:9px;white-space:pre-wrap;line-height:1.55;max-height:260px;overflow:auto">${m.besoin_brut}</div></section>` : ''}

          ${m.taille_client || m.region ? h`<div class="enc gl"><span class="sur">CE QU'UNE ANNONCE POURRA DIRE</span>
            <p>${m.secteur ?? 'secteur non précisé'} · ${m.taille_client ?? 'taille non précisée'}
               · ${m.region ?? 'région non précisée'}. Jamais le nom du client, jamais une ville.</p></div>` : ''}
        </div>
      </div>
    </div>`));

  // Le motif n'apparaît que lorsqu'il devient obligatoire : un champ toujours visible
  // se remplit par habitude, et un motif écrit par habitude ne vaut rien.
  for (const select of document.querySelectorAll<HTMLSelectElement>('[data-st]'))
    select.addEventListener('change', () => {
      const champ = $(`#mo-${select.dataset['st']}`) as HTMLInputElement | null;
      if (champ) champ.hidden = !REFUS_CANDIDATURE.includes(select.value as StatutCandidature);
    });

  $('#doc-mission')?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const fichier = ($('#doc-m-fichier') as HTMLInputElement | null)?.files?.[0];
    if (!fichier) return;
    const forme = new FormData();
    forme.set('fichier', fichier);
    try {
      await api.post(`/api/missions/${id}/document`, forme);
      mot('Document joint à la mission.');
      await ecranMission(id, session, chrome);
    } catch (e) { mot(direRefus(e)); }
  });

  $('#rattacher')?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const q = valeur('#q-profil');
    if (!q) return;
    try {
      type Trouve = { id: string; prenom: string; nom: string; role_libelle: string | null };
      const r = await api.get<{ profils: Trouve[] }>(
        '/api/profils' + parametres({ q, statut: ['qualifie'] }));
      const dejas = new Set(d.candidatures.map((c) => c.profil_id));

      if (r.profils.length) {
        peindre('#resultats', h`<div class="rang" style="flex-wrap:wrap;gap:6px;margin-top:9px">
          ${r.profils.slice(0, 12).map((p) => dejas.has(p.id)
            ? h`<span class="puce sourd">${p.prenom} ${p.nom} · déjà rattaché</span>`
            : h`<button class="puce" data-act="rattacher" data-id="${p.id}" data-mission="${id}">
                + ${p.prenom} ${p.nom}</button>`)}</div>`);
        return;
      }

      // Rien de qualifié : la vraie question est alors « personne, ou personne d'entretenu ? ».
      // Les deux appellent des gestes opposés — élargir la recherche, ou aller faire un
      // entretien — et un « aucun résultat » sec ne permet pas de choisir.
      const tous = await api.get<{ profils: Trouve[] }>('/api/profils' + parametres({ q }));
      const aQualifier = tous.profils.filter((p) => !dejas.has(p.id));
      peindre('#resultats', aQualifier.length
        ? h`<div class="enc ar" style="margin-top:9px">
            <span class="sur">${aQualifier.length} PROFIL${aQualifier.length > 1 ? 'S' : ''}
              CORRESPOND${aQualifier.length > 1 ? 'ENT' : ''}, MAIS SANS ENTRETIEN</span>
            <p>${aQualifier.slice(0, 6).map((p) => `${p.prenom} ${p.nom}`).join(', ')}${
              aQualifier.length > 6 ? '…' : ''}. Le passage à l'état qualifié exige un compte
              rendu d'entretien au dossier ; ouvrez la fiche pour l'y joindre.</p></div>`
        : h`<p class="sm sourd" style="margin-top:9px">Personne ne correspond dans le vivier.</p>`);
    } catch (e) { mot(direRefus(e)); }
  });
}
